import { faGrid2 } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { catchAll } from '@sorare/routing';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Collection } from '@sorare/core/src/atoms/icons/Collection';
import { Vault } from '@sorare/core/src/atoms/icons/Vault';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { BodyM } from '@sorare/core/src/atoms/typography';
import {
  ANY_SPORT_MY_CARDS_CARDS,
  ANY_SPORT_MY_CARDS_CARDS_TYPE,
  ANY_SPORT_MY_CARDS_COLLECTIONS,
  ANY_SPORT_MY_CARDS_COLLECTIONS_TYPE,
  MLB_MY_CARDS_VAULT as ANY_SPORT_MY_CARDS_VAULT,
  MLB_MY_CARDS_VAULT_TYPE as ANY_SPORT_MY_CARDS_VAULT_TYPE,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { navLabels } from '@sorare/core/src/lib/glossary';

import { SegmentButton } from '@sorare/marketplace/src/searchCards/Segment/ui';

import { useEnableCommonCollections } from 'hooks/my-cards/useEnableCommonCollections';
import { MyCardsLayoutContext } from 'layouts/my-cards/MyCardsLayout/MyCardsLayoutContext';

type Props = {
  rarity: Nullable<Rarity>;
};

export const CollectionNavigationSwitch = ({ rarity }: Props) => {
  const location = useLocation();
  const {
    flags: { useMlbSealing = false },
  } = useFeatureFlags();
  const { generateSportContextPath, sport } = useSportContext();

  const isCollection = location.pathname.includes(
    ANY_SPORT_MY_CARDS_COLLECTIONS
  );
  const showVault = useMlbSealing && sport === Sport.BASEBALL;
  const isVault = location.pathname.includes(ANY_SPORT_MY_CARDS_VAULT);

  const { defaultRarity } = useContext(MyCardsLayoutContext);
  const { currentUser } = useCurrentUserContext();
  const { cardCounts } = currentUser || {};
  const cardsCountByRarity = {
    [Rarity.common]: cardCounts?.common || 0,
    [Rarity.limited]: cardCounts?.limited || 0,
    [Rarity.rare]: cardCounts?.rare || 0,
    [Rarity.super_rare]: cardCounts?.superRare || 0,
    [Rarity.unique]: cardCounts?.unique || 0,
    [Rarity.custom_series]: cardCounts?.customSeries || 0,
  };
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const updateLastVisited =
    (tab: 'myCards' | 'myCollections' | 'myVault') => () => {
      updateLifecycle(LIFECYCLE.lastVisitedMyCardsTab, {
        ...(lifecycle?.[LIFECYCLE.lastVisitedMyCardsTab] || {}),
        [sport]: tab,
      });
    };
  const isTabletAndAbove = useIsTabletAndAbove();
  const { formatMessage } = useIntl();

  const isVaultEnabled = useMlbSealing && sport === Sport.BASEBALL;

  const cardCount = rarity && cardsCountByRarity?.[rarity];
  const rarityPath =
    (isVaultEnabled && rarity) || (cardCount && rarity) || defaultRarity;

  const myCardsTypePath = generateSportContextPath(
    ANY_SPORT_MY_CARDS_CARDS_TYPE,
    {
      params: {
        type: rarityPath,
      },
    }
  );

  const enableCommonCollections = useEnableCommonCollections();

  const collectionsTypePath = generateSportContextPath(
    ANY_SPORT_MY_CARDS_COLLECTIONS_TYPE,
    {
      params: {
        type:
          // we don't have common collections so switch to Limited upon visiting collections
          rarity === Rarity.common && !enableCommonCollections
            ? Rarity.limited
            : rarity || Rarity.limited,
      },
    }
  );
  const vaultTypePath = generateSportContextPath(
    ANY_SPORT_MY_CARDS_VAULT_TYPE,
    {
      params: {
        type:
          // we don't have common Vault so switch to Limited upon visiting Vault
          rarity === Rarity.common ? Rarity.limited : rarity || Rarity.limited,
      },
    }
  );

  if (isTabletAndAbove) {
    return (
      <Horizontal>
        <SegmentButton
          to={myCardsTypePath}
          className={classNames({ checked: !isCollection && !isVault })}
          onClick={updateLastVisited('myCards')}
        >
          <FontAwesomeIcon icon={faGrid2} />
          <BodyM bold>
            <FormattedMessage {...navLabels.gallery} />
          </BodyM>
        </SegmentButton>
        <SegmentButton
          to={collectionsTypePath}
          className={classNames({ checked: isCollection })}
          onClick={updateLastVisited('myCollections')}
        >
          <Collection />
          <BodyM bold>
            <FormattedMessage {...navLabels.collections} />
          </BodyM>
        </SegmentButton>
        {showVault && (
          <SegmentButton
            to={vaultTypePath}
            className={classNames({ checked: isVault })}
            onClick={updateLastVisited('myVault')}
          >
            <Vault size={2} />

            <BodyM bold>
              <FormattedMessage {...navLabels.vault} />
            </BodyM>
          </SegmentButton>
        )}
      </Horizontal>
    );
  }

  return (
    <TabBar forceMobileLayout compact>
      <Tab
        to={myCardsTypePath}
        matches={[catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_CARDS))]}
        onClick={updateLastVisited('myCards')}
      >
        <FontAwesomeIcon
          icon={faGrid2}
          title={formatMessage(navLabels.players)}
        />
      </Tab>
      <Tab
        to={collectionsTypePath}
        matches={[
          catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_COLLECTIONS)),
        ]}
        onClick={updateLastVisited('myCollections')}
      >
        <Collection title={formatMessage(navLabels.collections)} />
      </Tab>
      {showVault && (
        <Tab
          to={vaultTypePath}
          matches={[
            catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_VAULT)),
          ]}
          onClick={updateLastVisited('myVault')}
        >
          <Vault size={1.5} title={formatMessage(navLabels.vault)} />
        </Tab>
      )}
    </TabBar>
  );
};
